import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getNotifications } from "../apis/notifications/get.notifications";
import { Notification, NotificationType } from "../types/notification";
import { LAYOUT, COLORS, TRANSITIONS } from "../constants/layout";

const getNotificationUrl = (notification: Notification) => {
  switch (notification.type) {
    case NotificationType.NEW_RSVP:
      return "/guests";
    case NotificationType.ASO_EBI_SALE:
      return "/aso-ebi";
    case NotificationType.NEW_WELL_WISH:
      return "/love-wall";
    case NotificationType.PLANNER_TEAM:
      return "/settings/collaboration";
    default:
      return null;
  }
};

// Support replies/tickets are surfaced on the support icon (its own unread
// count + drawer), NOT in the general notification drawer. Filter them out so
// any legacy records never appear here.
const isSupportNotification = (n: Notification) =>
  n.type === NotificationType.SUPPORT_REPLY ||
  n.type === NotificationType.SUPPORT_TICKET;

const formatNotificationTime = (date: Date) => {
  const now = new Date();
  const diffInMinutes = Math.floor(
    (now.getTime() - date.getTime()) / (1000 * 60)
  );

  if (diffInMinutes < 1) return "just now";
  if (diffInMinutes < 60)
    return `${diffInMinutes} ${diffInMinutes === 1 ? "min" : "mins"} ago`;

  const hours = Math.floor(diffInMinutes / 60);
  if (hours < 24) return `${hours} ${hours === 1 ? "hour" : "hours"} ago`;

  // Check if it's yesterday
  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 1);

  if (date.toDateString() === yesterday.toDateString()) {
    return "yesterday";
  }

  // Format as "21 Nov 2025"
  const months = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];

  const day = date.getDate();
  const month = months[date.getMonth()];
  const year = date.getFullYear();

  return `${day} ${month} ${year}`;
};

interface NotificationDrawerTailwindProps {
  isOpen: boolean;
  onClose: () => void;
  isMobile?: boolean;
}

const NotificationDrawerTailwind: React.FC<NotificationDrawerTailwindProps> = ({
  isOpen,
  onClose,
  isMobile = false,
}) => {
  const navigate = useNavigate();

  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const hasMoreRef = useRef(true);
  const offsetRef = useRef(0);

  const limit = 10;

  const fetchNotifications = useCallback(
    async (isLoadMore = false) => {
      const currentOffset = isLoadMore ? offsetRef.current : 0;

      if (isLoadMore) {
        if (!hasMoreRef.current) return;
        setLoadingMore(true);
      } else {
        setLoading(true);
        setNotifications([]);
        offsetRef.current = 0;
        setError(null);
      }

      try {
        await getNotifications(
          {
            onCompleted: ({ data }) => {
              const newNotifications = data.notifications || [];

              if (isLoadMore) {
                setNotifications((prev) => [...prev, ...newNotifications]);
                offsetRef.current = currentOffset + limit;
              } else {
                setNotifications(newNotifications);
                offsetRef.current = limit;
              }
              hasMoreRef.current = newNotifications.length === limit;
            },
            onError: (err) => {
              console.error("Notifications API error:", err);
              setError(err || "Failed to fetch notifications");
            },
            query: {
              limit,
              offset: currentOffset,
            },
          },
          { bypassCache: true }
        );
      } catch (err) {
        console.error("Failed to fetch notifications:", err);
        setError("Failed to fetch notifications");
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [limit]
  );

  useEffect(() => {
    if (isOpen) {
      fetchNotifications();
    }
  }, [isOpen, fetchNotifications]);

  const handleScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
      const isNearBottom = scrollTop + clientHeight >= scrollHeight - 10;

      if (isNearBottom && hasMoreRef.current && !loadingMore && !loading) {
        fetchNotifications(true);
      }
    },
    [loadingMore, loading, fetchNotifications]
  );

  const getNotificationInitial = (message: string) => {
    return message.charAt(0).toUpperCase();
  };

  const handleNotificationClick = (notification: Notification) => {
    const url = getNotificationUrl(notification);
    if (url) {
      navigate(url);
      onClose(); // Close the drawer after navigation
    }
  };

  const visibleNotifications = notifications.filter(
    (n) => !isSupportNotification(n)
  );

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className={`fixed ${
          isMobile
            ? "top-0 left-0 right-0"
            : "inset-0"
        } ${COLORS.backdrop} backdrop-blur-sm`}
        style={{
          // The dashboard mobile header uses z-index 100. The notification
          // experience must cover it rather than appearing underneath it.
          zIndex: 109,
          ...(isMobile && { bottom: LAYOUT.mobileNavHeight }),
        }}
        onClick={onClose}
      />

      {/* Drawer */}
      <div
        className={`fixed flex flex-col ${
          isMobile
            ? `left-0 top-0 w-full ${COLORS.drawerBg} shadow-xl transform ${TRANSITIONS.default} ${
                isOpen ? "translate-y-0" : "translate-y-full"
              }`
            : `right-0 top-0 h-full w-full sm:w-96 ${COLORS.drawerBg} shadow-xl transform ${TRANSITIONS.default} ${
                isOpen ? "translate-x-0" : "translate-x-full"
              }`
        }`}
        style={{
          zIndex: 110,
          ...(isMobile && {
            bottom: LAYOUT.mobileNavHeight,
          }),
        }}
      >
        {/* Header */}
        <div className="flex shrink-0 items-center justify-between border-b border-gray-200 p-4 pt-[calc(1rem+env(safe-area-inset-top))]">
          <div className="flex-1 text-center">
            <h2 className="text-xl font-light text-gray-900">Notifications</h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
            aria-label="Close notifications"
          >
            <svg
              className="w-5 h-5 text-gray-500"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div
          className="flex min-h-0 flex-1 flex-col"
          style={{
            height: isMobile ? undefined : "calc(100vh - 73px)"
          }}
        >
          {loading ? (
            <div className="flex flex-col items-center justify-center h-48">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mb-3"></div>
              <p className="text-gray-600">Loading notifications...</p>
            </div>
          ) : error ? (
            <div className="flex flex-col items-center justify-center h-48">
              <svg
                className="w-8 h-8 text-red-500 mb-3"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.464 0L4.35 16.5c-.77.833.192 2.5 1.732 2.5z"
                />
              </svg>
              <p className="text-red-500 text-center">{error}</p>
            </div>
          ) : visibleNotifications.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-48">
              <svg
                className="w-12 h-12 text-gray-400 mb-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
                />
              </svg>
              <h3 className="text-lg font-medium text-gray-700 mb-2">
                No notifications yet
              </h3>
              <p className="text-gray-500 text-center">
                You'll see updates about your wedding here
              </p>
            </div>
          ) : (
            <div
              className="h-full overflow-y-auto overflow-x-hidden"
              onScroll={handleScroll}
              style={{
                WebkitOverflowScrolling: "touch",
                overscrollBehavior: "contain",
              }}
            >
              <div className="space-y-0">
                {visibleNotifications.map((notification, index) => {
                  const url = getNotificationUrl(notification);
                  const isClickable = !!url;

                  return (
                    <div
                      key={`${notification._id}-${index}`}
                      className={`p-4 border-b border-gray-100 transition-colors ${
                        isClickable
                          ? "hover:bg-gray-50 cursor-pointer"
                          : "hover:bg-gray-50"
                      }`}
                      onClick={() =>
                        isClickable && handleNotificationClick(notification)
                      }
                    >
                      <div className="flex items-center space-x-3">
                        <div className="flex-shrink-0">
                          <div className="w-14 h-14 rounded-full flex items-center justify-center border-2 border-[#E6439A]">
                            <div className="w-12 h-12 bg-[#B07793] rounded-full flex items-center justify-center overflow-hidden">
                              <span className="text-[10px] font-medium text-white dcalligraphy leading-none">
                                {getNotificationInitial(notification.message)}
                              </span>
                            </div>
                          </div>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between">
                            <p className="text-xs text-gray-700 line-clamp-2 flex-1 pr-2">
                              {notification.message}
                            </p>
                            <span className="text-xs text-gray-500 flex-shrink-0">
                              {formatNotificationTime(
                                new Date(notification.createdAt)
                              )}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}

                {loadingMore && (
                  <div className="flex flex-col items-center py-4">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500 mb-2"></div>
                    <p className="text-sm text-gray-500">Loading more...</p>
                  </div>
                )}

                {!hasMoreRef.current && notifications.length > 0 && (
                  <div className="text-center py-4">
                    <p className="text-sm text-gray-400">
                      You've reached the end
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default NotificationDrawerTailwind;
